import { useMemo, useCallback, useEffect, useRef } from "react";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { Tooltip } from "@fluentui/react-components";
import { FluentBadge } from "../../../../../components/FluentUI/Fluent-Badge";
import {
  ProjectIdCell,
  EnergyBankerEmptyState,
  EnergyBankerNoDealOnFilterState,
  EnergyBankerNoDealOnSearchState,
  renderCellContent,
  highlightMatch,
  getAccordionRowRender,
  applySearchFilter,
  getFlattenedData,
  getFilteredSortedData,
  useGroupToggle,
  makeDecimalCellHelper,
} from "./energy-banker-time-log-helper";
import {
  renderTooltipHeader,
  createColumnMenu,
} from "../../../dashboard-util/dashboard-telerik-helper/dashboard-telerik-helper";
import { isFilteredColumn } from "../../../dashboard-util/dashboard-telerik-helper/widget-grid-filter";
import PropTypes from "prop-types";
import DealTeamAvatarGroupCell from "../../../dashboard-util/dashboard-telerik-helper/deal-team-avatar";
import "./energy-banker-time-log.scss";

const EnergyBankerDetailGrid = ({
  data = [],
  onItemChange,
  canCreate,
  canEdit,
  detailState,
  onDetailStateChange,
  searchValue,
  onVisibleDataChange,
  resetDataState,
  setShowGlobalSearchModal,
}) => {
  const hasMounted = useRef(false);
  const { sort, filter } = detailState;
  const { groupState, toggleGroup, setGroupState } = useGroupToggle();
  const flattenedData = useMemo(() => getFlattenedData(data), [data]);
  const filteredData = useMemo(() => getFilteredSortedData(flattenedData, filter, sort), [flattenedData, filter, sort]);
  const visibleData = useMemo(() => filteredData, [filteredData]);
  const visibleLength = useMemo(() => visibleData.length, [visibleData.length]);

  useEffect(() => {
    if (hasMounted.current) {
      applySearchFilter(searchValue, detailState, onDetailStateChange, setGroupState);
    } else {
      hasMounted.current = true;
    }
  }, [searchValue]);

   useEffect(() => {
    if (onVisibleDataChange) {
      onVisibleDataChange({
        isEmpty: visibleData.length === 0,
        searchValue,
      });
    }
  }, [visibleData, searchValue, onVisibleDataChange]);

  const columnMenu = useMemo(() => createColumnMenu(flattenedData, searchValue), [flattenedData, searchValue]);
  const columnIsFiltered = useCallback(
    (fieldName) => isFilteredColumn(fieldName, filter),
    [filter]
  );
  const AccordionRowRender = useMemo(() => getAccordionRowRender(filteredData, groupState, toggleGroup,8), [filteredData, groupState]);
  const isEmpty = visibleData.length === 0;

  const finalGridData = isEmpty
    ? [{
        isEmptyRow: true,
        isSearchEmptyState: searchValue?.trim() !== "",
        isFilterEmptyState: searchValue?.trim() === "" && (filter?.filters?.length ?? 0) > 0,
      }]
    : visibleData;

  const makeDecimalCell = useMemo(() => makeDecimalCellHelper(visibleLength),[visibleLength] );
  const LastWeekCell = useMemo(
    () => makeDecimalCell("LastWeekHours", "InitiallyNull_LastWeekHours"),
    [makeDecimalCell]
  );
  const WeekendCell = useMemo(
    () => makeDecimalCell("WeekendHours", "InitiallyNull_WeekendHours"),
    [makeDecimalCell]
  );
  const NextWeekCell = useMemo(
    () => makeDecimalCell("NextWeekHours", "InitiallyNull_NextWeekHours"),
    [makeDecimalCell]
  );

  return (
    <div className="detail-grid-scroll-container jr-banker-time-log-detail-grid">
      <Grid
        data={finalGridData}
        rowRender={AccordionRowRender}
        sortable={true}
        sort={detailState.sort}
        filter={detailState.filter}
        onSortChange={(e) => onDetailStateChange({ ...detailState, sort: e.sort })}
        onFilterChange={(e) =>
          onDetailStateChange({ ...detailState, filter: e.filter })
        }
        editable="true"
        editField="inEdit"
        onItemChange={onItemChange}
        navigable={true}
        keyboardNavigation={true}
        dataItemKey="ProjectId"
        resizable={true}
      >
        <Column
          field="ProjectId"
          editable={false}
          title={renderTooltipHeader("#")}
          filter="text"
          columnMenu={columnMenu}
          headerClassName={columnIsFiltered("ProjectId") ? "filtered" : ""}
          cell={(props) =>
            props.dataItem.isEmptyRow ? (
              <td colSpan={8} className="text-center k-table-td">
                {props.dataItem.isSearchEmptyState ? (
                  <EnergyBankerNoDealOnSearchState
                    resetDataState={resetDataState}
                    setShowGlobalSearchModal={setShowGlobalSearchModal}
                  />
                ) : props.dataItem.isFilterEmptyState ? (
                  <EnergyBankerNoDealOnFilterState resetDataState={resetDataState} />
                ) : (
                  <EnergyBankerEmptyState canCreate={canCreate} />
                )}
              </td>
            ) : (
              <td className="background-white k-table-td">
                <ProjectIdCell
                  {...props}
                  canEdit={canEdit}
                  isLive={props.dataItem.GroupName === "Live"}
                />
              </td>
            )
          }
        />
        <Column
          field="ProjectName"
          title={renderTooltipHeader("Deal Name")}
          editable={false}
          filter="text"
          columnMenu={columnMenu}
          headerClassName={columnIsFiltered("ProjectName") ? "filtered" : ""}
          cell={(props) =>
            renderCellContent(visibleData.length, null, (
              <td className="background-white k-table-td">
                <Tooltip content={props.dataItem.ProjectName} relationship="label">
                  <div className="project-name-wrapper">
                    <span className="project-name-text">
                      {highlightMatch(props.dataItem.ProjectName, searchValue)}
                    </span>
                    {props.dataItem.IsOverdue && (
                    <FluentBadge
                      appearance="filled"
                      size="small"
                      color="warning"
                      text="Overdue"
                    />
                  )}
                  </div>
                </Tooltip>
              </td>
            ))
          }
        />
        <Column
          field="Company"
          title={renderTooltipHeader("Company")}
          editable={false}
          filter="text"
          columnMenu={columnMenu}
          headerClassName={columnIsFiltered("Company") ? "filtered" : ""}
          cell={(props) =>
            renderCellContent(visibleData.length, null, (
              <td className="background-white k-table-td">
                <Tooltip content={props.dataItem.Company} relationship="label">
                  <span>{highlightMatch(props.dataItem.Company, searchValue)}</span>
                </Tooltip>
              </td>
            ))
          }
        />
        <Column
          field="LastWeekHours"
          title={renderTooltipHeader("Last Wk's Hrs (M-F)")}
          filter="numeric"
          columnMenu={columnMenu}
          headerClassName={columnIsFiltered("LastWeekHours") ? "filtered" : ""}
          cell={LastWeekCell}
        />
        <Column
          field="WeekendHours"
          title={renderTooltipHeader("Last Wknd Hrs (S-S)")}
          filter="numeric"
          columnMenu={columnMenu}
          headerClassName={columnIsFiltered("WeekendHours") ? "filtered" : ""}
          cell={ WeekendCell }
        />
        <Column
          field="ActualTotalHours"
          title={renderTooltipHeader("Actual Total Hrs")}
          editable={false}
          filter="numeric"
          columnMenu={columnMenu}
          headerClassName={columnIsFiltered("ActualTotalHours") ? "filtered" : ""}
          cell={(props) => renderCellContent(visibleData.length, null, <td className="background-white k-table-td">{props.dataItem.ActualTotalHours ?? "—"}</td>)}
        />
        <Column
          field="NextWeekHours"
          title={renderTooltipHeader("Current Est. of Hrs (Nxt 7 Days)")}
          filter="numeric"
          columnMenu={columnMenu}
          headerClassName={columnIsFiltered("NextWeekHours") ? "filtered" : ""}
          cell={ NextWeekCell }
        />
        <Column
            field="Members"
            title={renderTooltipHeader("Deal Team")}
            filter="text"
            columnMenu={columnMenu}
            headerClassName={columnIsFiltered("Members") ? "filtered" : ""}
            cell={(props) =>
                renderCellContent(visibleData.length, null, (
                <td className="background-white k-table-td">
                    {props.dataItem.Members?.length ? (
                      <DealTeamAvatarGroupCell members={props.dataItem.Members} />
                    ) : (
                    "-"
                    )}
                </td>
                ))
            }
        />
      </Grid>
    </div>
  );
};

export default EnergyBankerDetailGrid;

EnergyBankerDetailGrid.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      ProjectId: PropTypes.number,
      ProjectName: PropTypes.string,
      Company: PropTypes.string,
      GroupName: PropTypes.string,
      IsOverdue: PropTypes.bool,
      LastWeekHours: PropTypes.number,
      WeekendHours: PropTypes.number,
      ActualTotalHours: PropTypes.number,
      NextWeekHours: PropTypes.number,
      Members: PropTypes.arrayOf(
        PropTypes.shape({
          User: PropTypes.shape({
            Id: PropTypes.number.isRequired,
            Name: PropTypes.string.isRequired,
          }).isRequired,
          IsOwner: PropTypes.bool.isRequired,
          IsSeniorDealMember: PropTypes.bool,
          IsStaffer: PropTypes.bool,
        })
      ),
      isEmptyRow: PropTypes.bool,
      showSearchEmptyState: PropTypes.bool,
    })
  ).isRequired,
  onItemChange: PropTypes.func.isRequired,
  canCreate: PropTypes.bool.isRequired,
  canEdit: PropTypes.bool.isRequired,
  searchValue: PropTypes.string,
  onVisibleDataChange: PropTypes.func,
  resetDataState: PropTypes.func.isRequired,
  setShowGlobalSearchModal: PropTypes.func.isRequired,
  detailState: PropTypes.shape({
    sort: PropTypes.arrayOf(
      PropTypes.shape({
        field: PropTypes.string,
        dir: PropTypes.oneOf(["asc", "desc"]),
      })
    ),
    filter: PropTypes.object,
  }).isRequired,
  onDetailStateChange: PropTypes.func.isRequired,
};
